<script setup lang="ts">
import { copyToClipboard } from '@/utils/apostatudo/clipboard';
import {
  cardParticles,
  compactXp,
  hexAlpha,
  mixColor,
  type LevelTier,
} from '@/utils/levels';

// Cartão do jogador na composição do protótipo desktop: emblema com o avatar
// embutido flutuando 34px acima da borda de cima, nível à esquerda, distância
// pro próximo à direita, nome + ID no meio e a barra de XP fechando. O emblema
// sair pra fora da caixa é o que obriga a folga de 48px acima do cartão.
const props = defineProps<{
  tier: LevelTier;
  /** Nível cru — o VIP mostra "Nível 35" na segunda linha. */
  level: number;
  levelLabel: string;
  nextLevelLabel: string;
  progress: number;
  xpToNext: number;
  isMaxLevel: boolean;
  playerName: string;
  playerId: string;
  avatar: string;
}>();

// Mesmo contrato do cartão mobile: o lápis avisa a tela, que abre o deeplink
// de apelido da Smartico.
const emit = defineEmits<{ editName: [] }>();

const cardBg = computed(
  () =>
    `linear-gradient(168deg,${mixColor(props.tier.color, '#05091A', 0.2)} 0%,${mixColor(
      props.tier.color,
      '#05091A',
      0.58,
    )} 46%,${mixColor(props.tier.color, '#05091A', 0.9)} 100%)`,
);

const strokeGradient = computed(
  () =>
    `linear-gradient(100deg,rgba(255,255,255,.1) 0%,#D8DEE9 12%,${props.tier.color} 30%,rgba(255,255,255,.14) 46%,${props.tier.accent} 62%,#EAEEF5 78%,rgba(255,255,255,.1) 100%)`,
);

/** Segunda linha: no VIP o nome do elo já é o título, então mostra o nível. */
const tierLine = computed(() =>
  props.tier.isVip ? `Nível ${props.level}` : props.tier.name,
);

const gapLine = computed(() =>
  props.isMaxLevel
    ? 'Topo da escala'
    : `Faltam ${compactXp(props.xpToNext)} XP`,
);

/** "Para o nível 12" em caixa baixa, mas "Para o VIP 3" preserva a sigla. */
const progressLabel = computed(() => {
  if (props.isMaxLevel) return 'Nível máximo alcançado';

  const label = props.nextLevelLabel;

  return `Para o ${label.startsWith('VIP') ? label : label.toLowerCase()}`;
});

/** Pontinhos que pulsam dentro do cartão, na cor do elo. */
const particles = computed(() => cardParticles(props.tier));

// Plaquinha do ID copia o número puro (sem o "#"), igual ao dropdown de perfil
// do header — é o valor que se cola na transferência.
const copied = ref(false);

let copiedTimer: ReturnType<typeof setTimeout> | undefined;

async function copyId() {
  const raw = props.playerId.replace('#', '');

  if (!(await copyToClipboard(raw))) return;

  copied.value = true;
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    copied.value = false;
  }, 2000);
}

onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<template>
  <div class="mt-[84px] flex w-full flex-col">
    <div
      class="levels-stroke relative flex min-w-0 flex-1 rounded-[18px] p-[1.4px]"
      :style="{
        background: strokeGradient,
        backgroundSize: '280% 100%',
        boxShadow: `0 14px 30px -24px ${hexAlpha(tier.color, 0.7)}`,
      }"
    >
      <div
        class="relative flex min-w-0 flex-1 flex-col rounded-[16.6px]"
        :style="{ background: cardBg }"
      >
        <!-- Brilho de canto + partículas + marca d'água do emblema (nesta
             ordem: a marca d'água fica por cima dos pontinhos) -->
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 overflow-hidden rounded-[16.6px]"
        >
          <div
            class="absolute inset-0"
            style="
              background: radial-gradient(
                120% 100% at 6% 0%,
                rgba(255, 255, 255, 0.06),
                transparent 58%
              );
            "
          ></div>
          <span
            v-for="(particle, index) in particles"
            :key="index"
            class="levels-pop absolute rounded-full"
            :style="{
              left: particle.left,
              top: particle.top,
              width: particle.size,
              height: particle.size,
              background: particle.color,
              boxShadow: `0 0 ${particle.blur} ${particle.color}`,
              animationDuration: particle.duration,
              animationDelay: particle.delay,
            }"
          ></span>
          <!-- `padding-top` em % cria o quadrado a partir da largura:
               `aspect-square` não gera CSS neste projeto. -->
          <div
            class="absolute left-1/2 top-1/2 w-[104%] -translate-x-1/2 -translate-y-1/2 bg-contain bg-center bg-no-repeat opacity-[0.05]"
            :style="{
              backgroundImage: `url(${tier.badge})`,
              paddingTop: '104%',
            }"
          ></div>
        </div>

        <div
          class="relative z-[1] flex flex-col gap-[13px] px-[22px] pb-[18px] pt-5"
        >
          <div class="relative flex items-start gap-[86px] pt-0.5">
            <div class="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
              <span
                class="truncate text-[17px] font-bold leading-[22px] tracking-[-0.025em] text-white"
              >
                {{ levelLabel }}
              </span>
              <span
                class="truncate text-[13px] font-medium leading-[18px] text-white/[0.72]"
              >
                {{ tierLine }}
              </span>
            </div>

            <!-- Emblema do elo com o avatar embutido, saindo pra fora do topo -->
            <span
              aria-hidden="true"
              class="pointer-events-none absolute left-1/2 top-[-96px] z-[2] block h-[150px] w-[150px] -translate-x-1/2"
            >
              <span
                class="absolute left-[14%] top-[16%] h-[72%] w-[72%] rounded-full blur-[10px]"
                :style="{
                  background: `radial-gradient(${hexAlpha(tier.color, 0.3)},transparent 66%)`,
                }"
              ></span>
              <span
                class="absolute inset-0 bg-contain bg-center bg-no-repeat"
                :style="{
                  backgroundImage: `url(${tier.badge})`,
                  filter: 'drop-shadow(0 6px 18px rgba(0,0,0,.6))',
                }"
              ></span>
              <span
                class="absolute left-[26%] top-[32%] h-[48%] w-[48%] rounded-full p-[2.5px]"
                :style="{
                  background: `linear-gradient(140deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.38)})`,
                  boxShadow: `0 0 18px ${hexAlpha(tier.color, 0.3)},0 6px 16px rgba(0,0,0,.45)`,
                }"
              >
                <!--
                  Avatar e disco na MESMA propriedade, em duas camadas: com
                  `background` (atalho) seguido de `background-image` a segunda
                  regra apagava o gradiente, e o avatar ficava recortado sobre o
                  fundo do cartão.
                -->
                <span
                  class="block h-full w-full rounded-full"
                  :style="{
                    backgroundImage: `url(${avatar}),radial-gradient(circle at 50% 34%,${mixColor(tier.color, '#050C22', 0.1)} 0%,${mixColor(
                      tier.color,
                      '#050C22',
                      0.48,
                    )} 46%,#050A18 100%)`,
                    backgroundSize: '88% auto,cover',
                    backgroundRepeat: 'no-repeat,no-repeat',
                    backgroundPosition: 'center bottom,center',
                    boxShadow: `inset 0 0 16px ${hexAlpha(tier.color, 0.5)}`,
                  }"
                ></span>
              </span>
            </span>

            <div
              class="flex min-w-0 flex-1 flex-col items-end gap-0.5 overflow-hidden"
            >
              <span
                class="truncate text-[17px] font-bold leading-[22px] tracking-[-0.025em] text-white"
              >
                {{ isMaxLevel ? 'Nível máximo' : 'Próximo nível' }}
              </span>
              <span
                class="truncate text-[13px] font-medium leading-[18px] text-white/[0.78]"
              >
                {{ gapLine }}
              </span>
            </div>
          </div>

          <div class="flex flex-wrap items-center justify-center gap-3">
            <!-- Nome, lápis e chip do ID numa linha só: os três com caixa
                 justa (leading-none / flex) pro `items-center` medir glifo
                 contra glifo — com line-height folgado o nome ganhava 5px de
                 caixa a mais e o chip do ID subia em relação a ele. -->
            <span
              class="whitespace-nowrap text-[19px] font-extrabold leading-none tracking-[-0.03em] text-white"
            >
              {{ playerName }}
            </span>
            <button
              aria-label="Editar apelido"
              class="flex flex-none items-center text-[16px] leading-none text-white/70 hover:text-white"
              type="button"
              @click="emit('editName')"
            >
              <NuxtIcon
                class="block"
                name="pen_square"
              />
            </button>
            <button
              v-if="playerId"
              aria-label="Copiar ID"
              class="relative flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[rgba(0,6,20,0.42)] px-3 py-[5px] text-[11px] font-semibold leading-none tracking-[0.07em] text-white/[0.76] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] tabular-nums"
              type="button"
              @click="copyId"
            >
              ID {{ playerId }}
              <NuxtIcon
                class="text-[11px] tracking-normal"
                name="copy"
              />
              <span
                v-if="copied"
                class="levels-copied absolute left-1/2 top-[calc(100%+5px)] -translate-x-1/2 rounded bg-[#00C9FF] px-1.5 py-0.5 text-[9px] font-medium tracking-normal text-white"
              >
                Copiado
              </span>
            </button>
          </div>

          <div class="flex flex-col gap-[7px]">
            <div class="flex items-baseline justify-between gap-3">
              <span
                class="text-[13px] font-medium leading-[18px] text-white/[0.72]"
              >
                {{ progressLabel }}
              </span>
              <span
                class="text-[19px] font-extrabold leading-6 tracking-[-0.025em] text-white tabular-nums"
              >
                {{ Math.round(progress) }}%
              </span>
            </div>
            <div
              class="h-[9px] overflow-hidden rounded-full bg-[rgba(0,5,20,0.6)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)]"
            >
              <div
                class="levels-bar h-full rounded-full"
                :style="{
                  width: `${progress}%`,
                  background: `linear-gradient(90deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.42)})`,
                  boxShadow: `0 0 10px ${hexAlpha(tier.color, 0.5)}`,
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* O reset global mata `transition`, então tudo aqui anda por keyframes. */
@keyframes levelsStrokeFlow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 280% 50%;
  }
}

.levels-stroke {
  animation: levelsStrokeFlow 7s linear infinite;
}

/* Pontinhos dentro do cartão: aparecem subindo um pouco e somem. */
@keyframes levelsPop {
  0%,
  100% {
    opacity: 0;
    transform: translateY(4px) scale(0.6);
  }
  45% {
    opacity: 0.9;
    transform: translateY(-3px) scale(1);
  }
}

.levels-pop {
  opacity: 0;
  animation-name: levelsPop;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}

@keyframes levelsBarGrow {
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
}

.levels-bar {
  transform-origin: left center;
  animation: levelsBarGrow 0.9s cubic-bezier(0.34, 1.2, 0.4, 1) both;
}

@keyframes levelsCopiedIn {
  0% {
    opacity: 0;
    transform: translate(-50%, -3px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.levels-copied {
  animation: levelsCopiedIn 0.18s ease-out both;
}
</style>
